import { PermissionFeature, PermissionFeatureToActions } from "@/protocols/permission"

export type PermissionTranslatedData = {
	label: string
	description: string
}

const permissionsFeatureToPermissionTranslated: {
	[FeatureType in PermissionFeature]: Record<PermissionFeatureToActions[FeatureType], PermissionTranslatedData>
} = {
	system: {
		admin: {
			label: "Administrador",
			description: "Quando habilitado, concede acesso total ao sistema, ignorando todas as demais permissões."
		}
	},
	chat: {
		"view-chat-bot-queue": {
			label: "Ver atendimentos do Bot",
			description: "Quando habilitado, permite visualizar as conversas que estão na fila do Bot."
		},
		"view-message-blast-queue": {
			label: "Ver fila de Envio em massa",
			description: "Quando habilitado, permite visualizar as conversas que estão na fila de Envio em massa."
		},
		"view-integrations-queue": {
			label: "Ver fila de Integrações",
			description: "Quando habilitado, permite visualizar as conversas que estão na fila de Integrações."
		},
		"view-archived-queue": {
			label: "Ver atendimentos finalizados",
			description: "Quando habilitado, permite visualizar atendimentos que estão na fila de finalizados."
		},
		"view-other-attendant-attendances": {
			label: "Ver atendimentos de outros atendentes",
			description: "Quando habilitado, permite visualizar os atendimentos em andamento de qualquer atendente da instância."
		},
		"filter-by-team": {
			label: "Filtrar chats por equipe",
			description: "Quando habilitado, permite filtrar chats por equipe."
		},
		"filter-by-attendant": {
			label: "Filtrar chats por lista de atendentes",
			description: "Quando habilitado, permite filtrar chats por lista de atendentes."
		},
		"finish-others-attendance": {
			label: "Finalizar atendimento de outro",
			description: "Quando habilitado, permite finalizar atendimentos em andamento de outros atendentes."
		},
		"transfer-any-chat": {
			label: "Transferir chat de outro atendente",
			description: "Quando habilitado, permite transferir chats que já possuem atendente designado."
		},
		"delete-chat": {
			label: "Excluir conversa",
			description: "Quando habilitado, permite excluir conversas do atendimento."
		},
		"delete-message": {
			label: "Apagar mensagem",
			description: "Quando habilitado, permite apagar mensagens específicas de uma conversa."
		},
		"start-attendance": {
			label: "Iniciar atendimento",
			description: "Quando habilitado, permite iniciar um novo atendimento a partir do botão de novo chat."
		}
	},
	role: {
		create: {
			label: "Criar cargo",
			description: "Quando habilitado, permite criar novos cargos."
		},
		update: {
			label: "Atualizar cargo",
			description: "Quando habilitado, permite editar cargos existentes."
		},
		delete: {
			label: "Deletar cargo",
			description: "Quando habilitado, permite excluir cargos."
		}
	},
	attendant: {
		delete: {
			label: "Excluir atendente",
			description: "Quando habilitado, permite excluir atendentes da instância."
		},
		update: {
			label: "Editar atendente",
			description: "Quando habilitado, permite editar dados de atendentes."
		},
		invite: {
			label: "Convidar atendente",
			description: "Quando habilitado, permite convidar novos atendentes para a instância."
		},
		block: {
			label: "Bloquear atendente",
			description: "Quando habilitado, permite bloquear e desbloquear atendentes."
		}
	},
	instance: {
		update: {
			label: "Editar instância",
			description: "Quando habilitado, permite alterar os dados da instância."
		}
	},
	"manager-panel": {
		access: {
			label: "Acessar Painel do Gestor",
			description: "Quando habilitado, permite acessar o Painel do Gestor e ver o botão de acesso no inbox."
		},
		"subscriptions-access": {
			label: "Acessar tela de assinaturas",
			description: "Quando habilitado, permite visualizar os dados de assinatura da instância."
		}
	},
	"admin-console": {
		access: {
			label: "Acessar Admin Console",
			description: "Quando habilitado, permite acessar o Admin Console."
		}
	},
	profile: {
		update: {
			label: "Editar perfil",
			description: "Quando habilitado, permite que o próprio usuário edite seu e-mail, nome e altere sua senha."
		}
	},
	"contact-note": {
		create: {
			label: "Criar nota de contato",
			description: "Quando habilitado, permite criar notas de contato."
		},
		"edit-others": {
			label: "Editar notas de outro usuário",
			description: "Quando habilitado, permite editar notas de contato criadas por outros usuários."
		},
		"delete-others": {
			label: "Deletar notas de outro usuário",
			description: "Quando habilitado, permite excluir notas de contato criadas por outros usuários."
		}
	},
	"custom-field": {
		create: {
			label: "Criar campo personalizado",
			description: "Quando habilitado, permite criar novos campos personalizados."
		},
		update: {
			label: "Editar campo personalizado",
			description: "Quando habilitado, permite editar campos personalizados existentes."
		},
		delete: {
			label: "Excluir campo personalizado",
			description: "Quando habilitado, permite excluir campos personalizados."
		}
	},
	"quick-reply": {
		create: {
			label: "Criar resposta rápida",
			description: "Quando habilitado, permite criar novas respostas rápidas."
		},
		update: {
			label: "Editar resposta rápida",
			description: "Quando habilitado, permite editar respostas rápidas existentes."
		},
		delete: {
			label: "Excluir resposta rápida",
			description: "Quando habilitado, permite excluir respostas rápidas."
		}
	},
	channel: {
		edit: {
			label: "Editar configurações de canal",
			description: "Quando habilitado, permite editar as configurações de um canal, incluindo dados extras e configurações de horário e delay."
		},
		disconnect: {
			label: "Desconectar canal",
			description: "Quando habilitado, permite desconectar um canal conectado à instância."
		},
		connect: {
			label: "Conectar canal",
			description: "Quando habilitado, permite conectar ou reconectar um canal à instância."
		}
	},
	"message-blast": {
		create: {
			label: "Criar envio em massa",
			description: "Quando habilitado, permite criar novos envios em massa."
		},
		update: {
			label: "Editar envio em massa",
			description: "Quando habilitado, permite editar envios em massa existentes."
		},
		delete: {
			label: "Excluir envio em massa",
			description: "Quando habilitado, permite excluir envios em massa."
		},
		"cancel-scheduled": {
			label: "Cancelar envio agendado",
			description: "Quando habilitado, permite cancelar envios em massa agendados que ainda não foram disparados."
		},
		schedule: {
			label: "Agendar envio em massa",
			description: "Quando habilitado, permite agendar o disparo de envios em massa."
		},
		fire: {
			label: "Disparar envio em massa",
			description: "Quando habilitado, permite disparar imediatamente envios em massa."
		}
	},
	"chat-bot": {
		create: {
			label: "Criar fluxo de Bot",
			description: "Quando habilitado, permite criar novos fluxos de Bot."
		},
		update: {
			label: "Editar fluxo de Bot",
			description: "Quando habilitado, permite editar fluxos de Bot existentes."
		},
		delete: {
			label: "Excluir fluxo de Bot",
			description: "Quando habilitado, permite excluir fluxos de Bot."
		},
		duplicate: {
			label: "Duplicar fluxo de Bot",
			description: "Quando habilitado, permite duplicar fluxos de Bot existentes."
		},
		export: {
			label: "Exportar fluxo de Bot",
			description: "Quando habilitado, permite exportar fluxos de Bot."
		}
	},
	contact: {
		create: {
			label: "Criar contato",
			description: "Quando habilitado, permite criar novos contatos."
		},
		update: {
			label: "Editar contato",
			description: "Quando habilitado, permite editar dados de contatos."
		},
		disable: {
			label: "Excluir contato",
			description: "Quando habilitado, permite excluir contatos da instância."
		},
		"block-unblock-waba": {
			label: "Bloquear/desbloquear contato no WABA",
			description: "Quando habilitado, permite bloquear e desbloquear contatos no canal WABA."
		},
		export: {
			label: "Exportar contatos",
			description: "Quando habilitado, permite exportar contatos da instância."
		},
		"send-to-bot": {
			label: "Enviar contato para um bot",
			description: "Quando habilitado, permite enviar contatos para um bot."
		},
		"view-sensitive-data": {
			label: "Ver dados sensíveis do contato",
			description: "Quando habilitado, permite visualizar email, telefone e dados brutos recebidos de integrações/webhooks do contato. Quando desabilitado, esses dados aparecem mascarados ou o acesso é bloqueado."
		}
	},
	integration: {
		create: {
			label: "Criar integração",
			description: "Quando habilitado, permite criar novas integrações."
		},
		update: {
			label: "Editar integração",
			description: "Quando habilitado, permite editar integrações existentes."
		},
		delete: {
			label: "Excluir integração",
			description: "Quando habilitado, permite excluir integrações."
		}
	},
	tag: {
		create: {
			label: "Criar tag",
			description: "Quando habilitado, permite criar novas tags."
		},
		update: {
			label: "Editar tag",
			description: "Quando habilitado, permite editar tags existentes."
		},
		delete: {
			label: "Excluir tag",
			description: "Quando habilitado, permite excluir tags."
		}
	},
	team: {
		create: {
			label: "Criar equipe",
			description: "Quando habilitado, permite criar novas equipes."
		},
		update: {
			label: "Editar equipe",
			description: "Quando habilitado, permite editar equipes existentes."
		},
		delete: {
			label: "Excluir equipe",
			description: "Quando habilitado, permite excluir equipes."
		}
	},
	"waba-template": {
		create: {
			label: "Criar template WABA",
			description: "Quando habilitado, permite criar novos templates WABA."
		},
		update: {
			label: "Editar template WABA",
			description: "Quando habilitado, permite editar templates WABA existentes."
		},
		delete: {
			label: "Excluir template WABA",
			description: "Quando habilitado, permite excluir templates WABA."
		}
	},
	"trackable-link": {
		create: {
			label: "Criar link rastreável",
			description: "Quando habilitado, permite criar novos links rastreáveis."
		},
		update: {
			label: "Editar link rastreável",
			description: "Quando habilitado, permite editar links rastreáveis existentes."
		}
	},
	snooze: {
		manage: {
			label: "Agendar e cancelar soneca",
			description: "Quando habilitado, permite agendar e cancelar a soneca de conversas."
		}
	},
	plugin: {
		disconnect: {
			label: "Desconectar plugin",
			description: "Quando habilitado, permite desconectar plugins conectados à instância."
		},
		connect: {
			label: "Conectar plugin",
			description: "Quando habilitado, permite conectar plugins à instância."
		}
	},
	"board-metrics": {
		access: {
			label: "Acessar Métricas de Vendas",
			description: "Quando habilitado, permite visualizar a página de Métricas de Vendas."
		},
		"edit-config": {
			label: "Editar configurações de Métricas de Vendas",
			description: "Quando habilitado, permite editar metas e normalização das Métricas de Vendas."
		}
	}
}

export const getPermissionTranslated = <Feature extends PermissionFeature>(feature: Feature, action: PermissionFeatureToActions[Feature]): PermissionTranslatedData => {
	return permissionsFeatureToPermissionTranslated[feature][action]
}
